import OBR from '@owlbear-rodeo/sdk';

const SOURCE = 'com.lordofdisndat.dcc.gm-bridge';
const READY_CHANNEL = 'dice-plus/isReady';
const REQUEST_CHANNEL = 'dice-plus/roll-request';

export interface ExternalRollRequest {
  notation: string;
  label?: string;
  target?: 'everyone' | 'self' | 'dm' | 'gm_only';
  showResults?: boolean;
  /** Result channel prefix. Defaults to the GM bridge's; the player sheet uses its own. */
  source?: string;
}

/** One notation group from a Dice+ result, with every die face in roll order. */
export interface DicePlusGroup {
  diceType: string;
  values: number[];
  kept: boolean[];
  condensed: boolean;
}

export type ExternalRollOutcome =
  | { ok: true; total: number; summary: string; notation: string; groups: DicePlusGroup[] }
  | { ok: false; error: string };

function readGroups(value: unknown): DicePlusGroup[] {
  if (!Array.isArray(value)) return [];
  return value.map((raw) => {
    const group = dataObject(raw);
    const dice = Array.isArray(group.dice) ? group.dice.map(dataObject) : [];
    return {
      diceType: typeof group.diceType === 'string' ? group.diceType : '',
      values: dice.map((d) => Number(d.value)),
      kept: dice.map((d) => d.kept !== false),
      condensed: group.isCondensed === true,
    };
  });
}

function dataObject(value: unknown): Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

export async function isDicePlusReady(timeoutMs = 1200): Promise<boolean> {
  const requestId = crypto.randomUUID();
  return new Promise<boolean>((resolve) => {
    let done = false;
    const finish = (value: boolean) => {
      if (done) return;
      done = true;
      unsubscribe();
      clearTimeout(timer);
      resolve(value);
    };
    const unsubscribe = OBR.broadcast.onMessage(READY_CHANNEL, (event) => {
      const data = dataObject(event.data);
      if (data.requestId === requestId && data.ready === true) finish(true);
    });
    const timer = setTimeout(() => finish(false), timeoutMs);
    void OBR.broadcast.sendMessage(READY_CHANNEL, { requestId, timestamp: Date.now() }, { destination: 'ALL' });
  });
}

export async function requestExternalRoll(request: ExternalRollRequest, timeoutMs = 15_000): Promise<ExternalRollOutcome> {
  const ready = await isDicePlusReady();
  if (!ready) return { ok: false, error: 'Dice+ is not loaded or did not answer the readiness check.' };

  const rollId = `dccgm_${Date.now().toString(36)}_${crypto.randomUUID()}`;
  const notation = request.label?.trim()
    ? `${request.notation.trim()} #${request.label.trim().replaceAll('#', '')}`
    : request.notation.trim();
  const playerName = await OBR.player.getName();

  return new Promise<ExternalRollOutcome>((resolve) => {
    let done = false;
    const finish = (value: ExternalRollOutcome) => {
      if (done) return;
      done = true;
      offResult();
      offError();
      clearTimeout(timer);
      resolve(value);
    };
    const source = request.source ?? SOURCE;
    const offResult = OBR.broadcast.onMessage(`${source}/roll-result`, (event) => {
      const data = dataObject(event.data);
      if (data.rollId !== rollId) return;
      const result = dataObject(data.result);
      const total = result.totalValue;
      finish({
        ok: true,
        total: typeof total === 'number' ? total : Number(total ?? 0),
        summary: typeof result.rollSummary === 'string' ? result.rollSummary : '',
        notation: typeof result.diceNotation === 'string' ? result.diceNotation : notation,
        groups: readGroups(result.groups),
      });
    });
    const offError = OBR.broadcast.onMessage(`${source}/roll-error`, (event) => {
      const data = dataObject(event.data);
      if (data.rollId !== rollId) return;
      finish({ ok: false, error: typeof data.error === 'string' ? data.error : 'Dice+ reported an unknown error.' });
    });
    const timer = setTimeout(() => finish({ ok: false, error: 'Dice+ did not return a result before the timeout.' }), timeoutMs);

    void OBR.broadcast.sendMessage(REQUEST_CHANNEL, {
      rollId,
      playerId: OBR.player.id,
      playerName,
      rollTarget: request.target ?? 'everyone',
      diceNotation: notation,
      showResults: request.showResults !== false,
      timestamp: Date.now(),
      source,
    }, { destination: 'ALL' });
  });
}
